/**
 * Utility functions for authentication and user session management
 */

/**
 * Gets a value from session storage with a default fallback
 * @param {string} key - The session storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} Parsed value from session storage or default value
 */
export const getSessionStorageOrDefault = (key, defaultValue) => {
  const stored = sessionStorage.getItem(key);
  if (!stored) {
    return defaultValue;
  }
  try {
    return JSON.parse(stored);
  } catch (error) {
    console.warn(`Failed to parse session storage value for key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Gets the current user from session storage
 * @returns {Object|null} User object or null if not found
 */
export const getCurrentUser = () => {
  return getSessionStorageOrDefault('user', null);
};

/**
 * Checks if the current user has a specific role
 * @param {string} role - Role to check for
 * @returns {boolean} True if user has the role
 */
export const hasRole = (role) => {
  const user = getCurrentUser();
  return user?.role === role;
};

/**
 * Checks if the current user is an admin
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  return hasRole('admin');
};

/**
 * Checks if the current user is a manager
 * @returns {boolean} True if user is manager
 */
export const isManager = () => {
  return hasRole('manager');
};

/**
 * Checks if the current user has admin or manager role
 * @returns {boolean} True if user is admin or manager
 */
export const isAdminOrManager = () => {
  return isAdmin() || isManager();
};